import React, { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Loader2, Play, RefreshCw, Calendar, AlertCircle } from 'lucide-react';
import { verticalsApi, ApiError } from '../../lib/api';
import type { VerticalsSummaryResponse } from '../../types/verticals';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { isTradeVerticalId, TRADE_VERTICAL_LABELS } from './verticalIds';

function pickStr(obj: Record<string, unknown> | null | undefined, keys: string[]): string | null {
  if (!obj) return null;
  for (const k of keys) {
    const v = obj[k];
    if (v != null && String(v).trim()) return String(v);
  }
  return null;
}

/**
 * Sprint 4 — structured War Room surface (still falls back to raw JSON for unknown fields).
 */
export default function VerticalStrategyTab() {
  const { verticalId } = useParams<{ verticalId: string }>();
  const vid = (verticalId || '').toLowerCase();
  const ok = isTradeVerticalId(vid);

  const [data, setData] = useState<VerticalsSummaryResponse | null>(null);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);
  const [runMsg, setRunMsg] = useState('');
  const [running, setRunning] = useState(false);
  const [diagnosing, setDiagnosing] = useState(false);

  const load = useCallback(async () => {
    if (!ok) return;
    setLoading(true);
    setErr('');
    try {
      const res = await verticalsApi.summary();
      setData(res);
    } catch (e) {
      setErr(e instanceof ApiError ? e.detail : e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [ok]);

  useEffect(() => {
    void load();
  }, [load]);

  const runWarRoom = async () => {
    if (!ok) return;
    setRunning(true);
    setRunMsg('');
    try {
      const r = await verticalsApi.runWarRoom(vid);
      setRunMsg(r.message || r.status || 'OK');
      await load();
    } catch (e) {
      setRunMsg(e instanceof ApiError ? e.detail : e instanceof Error ? e.message : String(e));
    } finally {
      setRunning(false);
    }
  };

  const diagnoseWarRoom = async () => {
    if (!ok) return;
    setDiagnosing(true);
    setRunMsg('');
    try {
      const r = await verticalsApi.diagnoseWarRoom(vid);
      if (r.healthy) {
        setRunMsg('✓ All pre-flight checks passed. If the run still fails, check server logs for the LLM call error.');
      } else {
        setRunMsg('⚠ Diagnosis found issues:\n' + r.issues.join('\n') + '\n\n' + JSON.stringify(r, null, 2));
      }
    } catch (e) {
      setRunMsg('Diagnose failed: ' + (e instanceof ApiError ? e.detail : e instanceof Error ? e.message : String(e)));
    } finally {
      setDiagnosing(false);
    }
  };

  if (!ok) return null;

  const slice = data?.verticals?.[vid];
  const last = slice?.last_war_room as Record<string, unknown> | null | undefined;
  const label = TRADE_VERTICAL_LABELS[vid];

  const ranAt = pickStr(last, ['run_at', 'completed_at', 'updated_at', 'timestamp', 'saved_at']);
  const strategyDate = pickStr(last, ['strategy_date', 'date', 'war_room_date']);
  const status = pickStr(last, ['status', 'state']);
  const legacyNote = pickStr(last, ['note']);
  const errLast = pickStr(last, ['error', 'last_error', 'failure']);
  const marketplace = pickStr(last, ['marketplace_analysis']);
  const macro = pickStr(last, ['economic_landscape']);
  const roadmap = pickStr(last, ['daily_goal_roadmap', 'summary']);
  const deploy = pickStr(last, ['deployment_plan', 'message']);
  const news = last?.news_catalysts;

  return (
    <div className="space-y-4 max-w-3xl">
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          <span className="ml-2">Refresh</span>
        </Button>
        <Button size="sm" onClick={() => void runWarRoom()} disabled={running}>
          {running ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Play className="w-4 h-4 mr-1" />}
          Run War Room ({label})
        </Button>
        <Button variant="outline" size="sm" onClick={() => void diagnoseWarRoom()} disabled={diagnosing}>
          {diagnosing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <AlertCircle className="w-4 h-4 mr-1" />}
          Diagnose
        </Button>
      </div>

      {err ? <div className="text-sm text-destructive border border-destructive/30 rounded p-3">{err}</div> : null}
      {runMsg ? (
        <div className="text-sm border rounded-md px-3 py-2 bg-muted/40 whitespace-pre-wrap">{runMsg}</div>
      ) : null}

      {loading && !data ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      ) : null}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Last War Room run
          </CardTitle>
          <CardDescription className="text-xs">
            When this vertical last completed a scoped War Room pipeline (metadata from Firestore).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          {!last || Object.keys(last).length === 0 ? (
            <p className="text-muted-foreground">No last_war_room recorded for this vertical yet.</p>
          ) : (
            <>
              <div className="flex flex-wrap gap-2 items-center">
                {status ? (
                  <Badge variant={status === 'failed' ? 'destructive' : 'secondary'}>{status}</Badge>
                ) : null}
                {strategyDate ? (
                  <span className="text-xs text-muted-foreground">
                    Strategy date: <span className="font-mono text-foreground">{strategyDate}</span>
                  </span>
                ) : null}
              </div>
              {ranAt ? (
                <div className="text-xs text-muted-foreground">
                  Run timestamp: <span className="font-mono text-foreground">{ranAt}</span>
                </div>
              ) : (
                <div className="text-xs text-amber-600 flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5" />
                  No run timestamp field found — see raw payload below.
                </div>
              )}
              {legacyNote ? (
                <div className="rounded-md border bg-muted/30 px-3 py-2 text-xs text-foreground whitespace-pre-wrap">
                  {legacyNote}
                </div>
              ) : null}
              {errLast ? (
                <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
                  {errLast}
                </div>
              ) : null}
              {marketplace ? (
                <div className="rounded-md border bg-muted/20 px-3 py-2 text-xs space-y-1">
                  <p className="font-medium text-foreground">Marketplace analysis</p>
                  <p className="text-muted-foreground whitespace-pre-wrap">{marketplace}</p>
                </div>
              ) : null}
              {macro ? (
                <div className="rounded-md border bg-muted/20 px-3 py-2 text-xs space-y-1">
                  <p className="font-medium text-foreground">Economic landscape</p>
                  <p className="text-muted-foreground whitespace-pre-wrap">{macro}</p>
                </div>
              ) : null}
              {Array.isArray(news) && news.length > 0 ? (
                <div className="rounded-md border bg-muted/20 px-3 py-2 text-xs space-y-1">
                  <p className="font-medium text-foreground">News & catalysts</p>
                  <ul className="list-disc pl-4 text-muted-foreground space-y-0.5">
                    {(news as unknown[]).slice(0, 12).map((n, i) => (
                      <li key={i}>{String(n)}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {roadmap ? (
                <div className="rounded-md border bg-muted/20 px-3 py-2 text-xs space-y-1">
                  <p className="font-medium text-foreground">Daily goal roadmap</p>
                  <p className="text-muted-foreground whitespace-pre-wrap">{roadmap}</p>
                </div>
              ) : null}
              {deploy ? (
                <div className="rounded-md border bg-muted/20 px-3 py-2 text-xs space-y-1">
                  <p className="font-medium text-foreground">Deployment plan</p>
                  <p className="text-muted-foreground whitespace-pre-wrap">{deploy}</p>
                </div>
              ) : null}
              <details className="text-xs">
                <summary className="cursor-pointer text-muted-foreground hover:text-foreground">Raw metadata</summary>
                <pre className="mt-2 font-mono overflow-auto max-h-[280px] bg-muted/30 rounded p-3 text-[11px]">
                  {JSON.stringify(last, null, 2)}
                </pre>
              </details>
            </>
          )}
        </CardContent>
      </Card>

      <p className="text-xs text-muted-foreground">
        Global War Room committee settings (stocks) live under{' '}
        <Link to="/ai?tab=war-room" className="text-primary underline-offset-4 hover:underline">
          AI Control Center
        </Link>
        . Vertical runs above are scoped to <span className="font-mono">{vid}</span>.
      </p>
    </div>
  );
}
